import './FacebookCandidates.scss';

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import ReviewModal from './modals/ReviewModal';

const STATUSES = ['pending', 'approved', 'rejected', 'skipped'];
const PER_PAGE = 25;

function RecommendationBadge({ value }) {
  const cls = {
    approve: 'badge--approve',
    review:  'badge--review',
    reject:  'badge--reject',
  }[value] || '';
  return <span className={`fb-badge ${cls}`}>{value || '—'}</span>;
}

export default function FacebookCandidates() {
  const [activeStatus, setActiveStatus] = useState('pending');
  const [candidates, setCandidates]     = useState([]);
  const [page, setPage]                 = useState(1);
  const [hasMore, setHasMore]           = useState(true);
  const [loading, setLoading]           = useState(false);
  const [selected, setSelected]         = useState(null);
  const [retraining, setRetraining]     = useState(false);
  const [retrainResult, setRetrainResult] = useState(null);

  const fetchCandidates = useCallback(async (status, pageNum) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/facebook-candidates?submission_status=${status}&page=${pageNum}&per_page=${PER_PAGE}`
      );
      setCandidates(prev => pageNum === 1 ? data : [...prev, ...data]);
      setHasMore(data.length === PER_PAGE);
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    setCandidates([]);
    setHasMore(true);
    fetchCandidates(activeStatus, 1);
  }, [activeStatus, fetchCandidates]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchCandidates(activeStatus, next);
  };

  const handleReviewed = (updated, decision) => {
    // Remove from current queue (decision moved it to another status)
    setCandidates(prev => prev.filter(c => c.id !== updated.id));
  };

  const handleRetrain = async () => {
    setRetraining(true);
    setRetrainResult(null);
    try {
      const { data } = await api.post('/facebook-candidates/retrain');
      setRetrainResult({ ok: true, ...data });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Retrain failed.';
      setRetrainResult({ ok: false, detail });
    } finally {
      setRetraining(false);
    }
  };

  return (
    <div className="__fb-candidates">
      <div className="__fb-candidates__container">

        <div className="__fb-candidates__header">
          <span>Facebook Candidates</span>
          <button
            className="ppy-btn ppy-btn-primary"
            onClick={handleRetrain}
            disabled={retraining}
          >
            {retraining ? 'Retraining…' : 'Retrain Model'}
          </button>
        </div>

        {retrainResult && (
          <div className={`__fb-candidates__retrain-result ${retrainResult.ok ? 'ok' : 'err'}`}>
            {retrainResult.ok
              ? `Model retrained on ${retrainResult.trained_on} examples. CV F1: ${retrainResult.cv_f1_mean ?? 'n/a'}. Valid: ${retrainResult.label_distribution?.valid}, Invalid: ${retrainResult.label_distribution?.invalid}`
              : retrainResult.detail}
          </div>
        )}

        {/* Status tabs */}
        <div className="__fb-candidates__tabs">
          {STATUSES.map(s => (
            <button
              key={s}
              className={`tab ${activeStatus === s ? 'tab--active' : ''}`}
              onClick={() => setActiveStatus(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <table className="__fb-candidates__table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Post (preview)</th>
              <th>Signal</th>
              <th>Confidence</th>
              <th>Group</th>
              <th>Author</th>
              <th>Saved by</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map(c => (
              <tr key={c.id} onClick={() => setSelected(c)} className="__fb-candidates__row">
                <td>{c.id}</td>
                <td className="cell--text">{c.original_raw_text?.slice(0, 120) || c.title}</td>
                <td><RecommendationBadge value={c.llm_recommendation} /></td>
                <td>
                  {c.llm_confidence != null
                    ? `${Math.round(c.llm_confidence * 100)}%`
                    : '—'}
                </td>
                <td>{c.facebook_group || '—'}</td>
                <td>{c.author || '—'}</td>
                <td>{c.saved_by || '—'}</td>
              </tr>
            ))}
            {!loading && candidates.length === 0 && (
              <tr>
                <td colSpan={7} className="__fb-candidates__empty">
                  No {activeStatus} candidates.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {loading && <div className="__fb-candidates__loading">Loading…</div>}

        {hasMore && !loading && candidates.length > 0 && (
          <div className="__fb-candidates__load-more">
            <button className="ppy-btn ppy-btn-secondary" onClick={loadMore}>Load more</button>
          </div>
        )}
      </div>

      <ReviewModal
        candidate={selected}
        onClose={() => setSelected(null)}
        onReviewed={handleReviewed}
      />
    </div>
  );
}
